import React, { useState, useEffect } from "react";
import { X, ArrowRight, Check, Calendar, Building2, Sparkles } from "lucide-react";
import { api, formatErr } from "../lib/api";

/**
 * Two public lead-generation modals used on Landing & DemoTour.
 * Replace direct registration: every submission lands in admin queue
 * (collection `business_leads`, status="pending"). Admin approves or rejects.
 *
 * Backend: POST /api/leads/demo   POST /api/leads/signup
 *
 * Both modals include a hidden honeypot `website` field; bots filling it get
 * silently dropped server-side.
 */

const BUSINESS_TYPES = [
  { v: "palestra", label: "Palestra" },
  { v: "centro_estetico", label: "Centro estetico" },
  { v: "parrucchiere", label: "Parrucchiere / barber" },
  { v: "studio_professionale", label: "Studio professionale" },
  { v: "altro", label: "Altro" },
];

function ModalShell({ open, onClose, title, eyebrow, icon: Icon, children, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm px-6 pt-6 pb-4 border-b border-zinc-100 dark:border-zinc-800 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400 mb-1.5 flex items-center gap-1.5">
              {Icon && <Icon className="h-3 w-3" />}
              {eyebrow}
            </div>
            <h3 className="font-display text-xl sm:text-2xl tracking-tight font-medium dark:text-zinc-100">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 inline-flex items-center justify-center text-zinc-500 dark:text-zinc-400 shrink-0"
            aria-label="Chiudi"
            data-testid="lead-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">{hint}</div>}
    </label>
  );
}

const inputCls =
  "w-full h-11 px-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const textareaCls =
  "w-full px-3.5 py-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none";

function SuccessState({ kind, onClose }) {
  return (
    <div className="text-center py-6" data-testid={`lead-success-${kind}`}>
      <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
        <Check className="h-6 w-6" />
      </div>
      <div className="font-display text-xl font-medium dark:text-zinc-100">Richiesta inviata</div>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2 max-w-sm mx-auto">
        {kind === "demo"
          ? "Grazie! Ti contatteremo entro 24h per fissare la demo nel giorno e fascia che preferisci."
          : "Grazie! Il nostro team valuterà la richiesta e ti contatteremo via email entro 24-48h con i prossimi passi."}
      </p>
      <button onClick={onClose} className="btn-secondary mt-6" data-testid="lead-success-close">
        Chiudi
      </button>
    </div>
  );
}

// ---------- Demo modal ----------
export function BookDemoModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    business_type: "centro_estetico",
    preferred_date: "", preferred_slot: "",
    notes: "",
    website: "", // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setDone(false); setErr(""); }
  }, [open]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr("Compila nome, email e telefono.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        business_type: form.business_type,
        preferred_date: form.preferred_date || null,
        preferred_slot: form.preferred_slot || null,
        notes: form.notes.trim() || null,
        website: form.website,
      };
      await api.post("/leads/demo", payload);
      setDone(true);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      eyebrow="Demo gratuita 30 min"
      title="Prenota una demo personalizzata"
      icon={Calendar}
      testid="book-demo-modal"
    >
      {done ? (
        <SuccessState kind="demo" onClose={onClose} />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="book-demo-form">
          <p className="text-sm text-zinc-600 dark:text-zinc-400 -mt-1">
            Ti mostriamo Prenotoo dal vivo, rispondiamo a tutte le domande e capiamo se è la soluzione giusta per te. Nessun impegno.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome e cognome" required>
              <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Mario Rossi" data-testid="demo-input-name" maxLength={80} />
            </Field>
            <Field label="Telefono" required>
              <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39 320 123 4567" data-testid="demo-input-phone" maxLength={30} />
            </Field>
          </div>

          <Field label="Email" required>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario@studio.it" data-testid="demo-input-email" />
          </Field>

          <Field label="Tipo di attività" required>
            <select className={inputCls} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="demo-input-type">
              {BUSINESS_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Data preferita" hint="Facoltativo">
              <input type="date" className={inputCls} value={form.preferred_date} min={today} onChange={(e) => set("preferred_date", e.target.value)} data-testid="demo-input-date" />
            </Field>
            <Field label="Fascia oraria" hint="Facoltativo">
              <select className={inputCls} value={form.preferred_slot} onChange={(e) => set("preferred_slot", e.target.value)} data-testid="demo-input-slot">
                <option value="">Indifferente</option>
                <option value="mattina">Mattina (9–13)</option>
                <option value="pomeriggio">Pomeriggio (14–18)</option>
                <option value="sera">Sera (18–20)</option>
              </select>
            </Field>
          </div>

          <Field label="Vuoi anticiparci qualcosa?" hint="Domande, esigenze specifiche, dimensione del team…">
            <textarea rows={3} className={textareaCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ho 2 collaboratori, gestisco circa 40 appuntamenti a settimana…" maxLength={500} data-testid="demo-input-notes" />
          </Field>

          {/* Honeypot — must remain invisible & empty */}
          <input
            type="text"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            className="absolute opacity-0 pointer-events-none h-0 w-0"
            aria-hidden="true"
            name="website"
          />

          {err && <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 rounded-lg" data-testid="demo-error">{err}</div>}

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1" data-testid="demo-cancel">Annulla</button>
            <button type="submit" disabled={loading} className="btn-accent flex-1 disabled:opacity-60" data-testid="demo-submit">
              {loading ? "Invio…" : <>Invia richiesta <ArrowRight className="h-4 w-4 ml-2" /></>}
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400 text-center mt-1">
            Ti contatteremo entro 24h al numero o email indicati.
          </p>
        </form>
      )}
    </ModalShell>
  );
}

// ---------- Signup modal ----------
export function RequestSignupModal({ open, onClose }) {
  const [form, setForm] = useState({
    business_name: "", business_type: "centro_estetico",
    owner_name: "", email: "", phone: "",
    vat_number: "", address: "",
    team_size: "",
    notes: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setDone(false); setErr(""); }
  }, [open]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!form.business_name.trim() || !form.owner_name.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr("Compila i campi obbligatori (attività, titolare, email, telefono).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        business_type: form.business_type,
        owner_name: form.owner_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        vat_number: form.vat_number.trim() || null,
        address: form.address.trim() || null,
        team_size: form.team_size || null,
        notes: form.notes.trim() || null,
        website: form.website,
      };
      await api.post("/leads/signup", payload);
      setDone(true);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      eyebrow="Attivazione su invito"
      title="Richiedi l'attivazione"
      icon={Building2}
      testid="signup-request-modal"
    >
      {done ? (
        <SuccessState kind="signup" onClose={onClose} />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="signup-request-form">
          <p className="text-sm text-zinc-600 dark:text-zinc-400 -mt-1">
            Lasciaci i dati della tua attività: il nostro team valuta ogni richiesta personalmente per garantire un onboarding curato. Ti rispondiamo entro 24-48h.
          </p>

          <Field label="Nome dell'attività" required>
            <input className={inputCls} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Studio Belle Milano" data-testid="signup-input-business" maxLength={120} />
          </Field>

          <Field label="Tipo di attività" required>
            <select className={inputCls} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="signup-input-type">
              {BUSINESS_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Titolare / Referente" required>
              <input className={inputCls} value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} placeholder="Giulia Rossi" data-testid="signup-input-owner" maxLength={80} />
            </Field>
            <Field label="Telefono" required>
              <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39 320 123 4567" data-testid="signup-input-phone" maxLength={30} />
            </Field>
          </div>

          <Field label="Email" required>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="info@studiobelle.it" data-testid="signup-input-email" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="P. IVA" hint="Facoltativo">
              <input className={inputCls} value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder="IT01234567890" data-testid="signup-input-vat" maxLength={30} />
            </Field>
            <Field label="Dimensione team">
              <select className={inputCls} value={form.team_size} onChange={(e) => set("team_size", e.target.value)} data-testid="signup-input-team">
                <option value="">Seleziona…</option>
                <option value="1">Solo io</option>
                <option value="2-5">2 – 5 persone</option>
                <option value="6-15">6 – 15 persone</option>
                <option value="16+">16 o più</option>
              </select>
            </Field>
          </div>

          <Field label="Indirizzo" hint="Facoltativo">
            <input className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Via Brera 12, Milano" data-testid="signup-input-address" maxLength={200} />
          </Field>

          <Field label="Note" hint="Cosa ti aspetti da Prenotoo, esigenze particolari…">
            <textarea rows={3} className={textareaCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ho già un sito, mi serve solo un sistema di prenotazioni online…" maxLength={500} data-testid="signup-input-notes" />
          </Field>

          <input
            type="text"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            className="absolute opacity-0 pointer-events-none h-0 w-0"
            aria-hidden="true"
            name="website"
          />

          {err && <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 rounded-lg" data-testid="signup-error">{err}</div>}

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1" data-testid="signup-cancel">Annulla</button>
            <button type="submit" disabled={loading} className="btn-accent flex-1 disabled:opacity-60" data-testid="signup-submit">
              {loading ? "Invio…" : <>Invia richiesta <ArrowRight className="h-4 w-4 ml-2" /></>}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

// ---------- Convenience: shared CTA pair ----------
export function LeadCtaButtons({ variant = "default", className = "" }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const isDark = variant === "dark";
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      <button
        type="button"
        onClick={() => setDemoOpen(true)}
        className={isDark ? "btn-accent" : "btn-accent"}
        data-testid="lead-cta-book-demo"
      >
        <Calendar className="h-4 w-4 mr-2" /> Prenota una demo
      </button>
      <button
        type="button"
        onClick={() => setSignupOpen(true)}
        className={isDark ? "h-11 px-5 rounded-full inline-flex items-center text-sm font-medium bg-white/10 hover:bg-white/20 text-white border border-white/20 transition" : "btn-secondary"}
        data-testid="lead-cta-request-signup"
      >
        <Sparkles className="h-4 w-4 mr-2" /> Richiedi attivazione
      </button>
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <RequestSignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  );
}
